import { ReactNode, Suspense, useEffect } from 'react'
import { BrowserRouter } from 'react-router'
import {
  Center,
  ColorSchemeScript,
  createTheme,
  Loader,
  MantineProvider,
} from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import { configureAxiosClient } from './axios-client'
import { initializePlatformFromMeta } from './auth/api'
import {
  initializeNotifications,
  registerStoredPushToken,
} from './mobile-notifications'
import { AppError, ErrorWithSentryId, formatZodError } from './common/errors'
import { ZodError } from 'zod'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/carousel/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/tiptap/styles.css'
import '@mantine/notifications/styles.css'
import { EditModeProvider } from './widgets/providers'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 2 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof AppError) {
          if (
            'shouldRetry' in error &&
            typeof error.shouldRetry === 'function'
          ) {
            return error.shouldRetry() && failureCount < 2
          }
        }
        return failureCount < 1
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error instanceof AppError) {
          if (
            'shouldRetry' in error &&
            typeof error.shouldRetry === 'function'
          ) {
            return error.shouldRetry() && failureCount < 2
          }
        }
        return failureCount < 1
      },
      onError: (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[Mutation Error]', error)
        }

        if (!(error instanceof AppError)) {
          let errorToReport = error

          if (error instanceof ZodError) {
            errorToReport = formatZodError(error)
          }

          if (process.env.REACT_SENTRY_ENVIRONMENT !== 'local') {
            const eventId =
              (errorToReport as AppError).reportToSentry?.() ||
              Sentry.captureException(errorToReport)

            if (error && typeof error === 'object') {
              ;(error as ErrorWithSentryId).sentryEventId = eventId
            }
          }
        }
      },
    },
  },
})

// Read platform_id from server-injected meta tag before any requests are made
initializePlatformFromMeta()

// Configure axios client asynchronously
configureAxiosClient(axios).catch((error) => {
  console.error('Failed to configure axios client:', error)
})

interface Props {
  children: ReactNode
}

const fontFallbackStack =
  "'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
const monoFallbackStack =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"

export const leaguestarTheme = createTheme({
  primaryColor: 'primary',
  fontFamily: fontFallbackStack,
  fontFamilyMonospace: monoFallbackStack,
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    xxl: '1.5rem',
    xxxl: '2rem',
  },
  headings: {
    fontFamily: fontFallbackStack,
  },
  spacing: {
    xxxs: '0.25rem',
    xxs: '0.5rem',
    xs: '0.75rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  colors: {
    primary: [
      '#e7f0f2',
      '#b3d1d6',
      '#8fbbc2',
      '#5c9ca7',
      '#3c8995',
      '#0b6b7b',
      '#0a6170',
      '#084c57',
      '#063b44',
      '#052d34',
    ],
    secondary: [
      '#e7f9f6',
      '#b3ece4',
      '#8ee3d7',
      '#5bd6c5',
      '#3bceba',
      '#0ac2a9',
      '#09b19a',
      '#078a78',
      '#066b5d',
      '#045147',
    ],
    error_red: [
      '#f7e7e7',
      '#e7b5b5',
      '#dc9191',
      '#cc5e5e',
      '#c23f3f',
      '#b30f0f',
      '#a30e0e',
      '#7f0b0b',
      '#620808',
      '#4b0606',
    ],
  },
})

export function AppProviders({ children }: Props) {
  useEffect(() => {
    const setupNotifications = async () => {
      await initializeNotifications()
      // Try to register any stored push token (will succeed if user is already logged in)
      await registerStoredPushToken()
    }
    setupNotifications()
  }, [])

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ColorSchemeScript defaultColorScheme="light" />
        <MantineProvider defaultColorScheme="light" theme={leaguestarTheme}>
          <Notifications />
          <ModalsProvider>
            <EditModeProvider>
              <Suspense fallback={<Splash />}>{children}</Suspense>
            </EditModeProvider>
          </ModalsProvider>
        </MantineProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

function Splash() {
  // class name is defined in global html head styles
  return (
    <div className="splash">
      <Center w="100%" h="100%">
        <Loader color="white" style={{ position: 'relative', top: '50px' }} />
      </Center>
    </div>
  )
}
